import React, { useState, useEffect, useRef } from 'react';

/**
 * Modal Avanzado para Capas UV (Ultravioleta)
 * Características invisibles que solo se ven bajo luz UV
 */
const UVLayerModal = ({ isOpen, onClose, onGenerate }) => {
    // Estados principales
    const [effectType, setEffectType] = useState('microdots');
    const [uvColor, setUvColor] = useState('#7c3aed'); // Violeta UV
    const [intensity, setIntensity] = useState(0.35);
    const [width, setWidth] = useState(260);
    const [height, setHeight] = useState(160);

    // Configuración de micro puntos
    const [dotDensity, setDotDensity] = useState(1200);
    const [dotSize, setDotSize] = useState(0.8);
    const [dotPattern, setDotPattern] = useState('random'); // random, grid, spiral

    // Configuración de guilloche
    const [guillocheWaves, setGuillocheWaves] = useState(5);
    const [guillocheFrequency, setGuillocheFrequency] = useState(17);
    const [guillocheAmplitude, setGuillocheAmplitude] = useState(0.42);
    const [guillocheLayers, setGuillocheLayers] = useState(3);

    // Configuración de marca de agua
    const [watermarkText, setWatermarkText] = useState('SECURITY • ORIGINAL • VALID');
    const [watermarkAngle, setWatermarkAngle] = useState(-30);
    const [watermarkSpacing, setWatermarkSpacing] = useState(60);
    const [watermarkFontSize, setWatermarkFontSize] = useState(22);

    // Configuración de malla anti-copia
    const [gridSpacing, setGridSpacing] = useState(12);
    const [gridStrokeWidth, setGridStrokeWidth] = useState(1.0);
    const [gridDiagonal, setGridDiagonal] = useState(true);

    // Características avanzadas
    const [enableFluorescence, setEnableFluorescence] = useState(true);
    const [fluorescenceColor, setFluorescenceColor] = useState('#00ffff');
    const [enablePhosphorescence, setEnablePhosphorescence] = useState(false);
    const [phosphorescenceDuration, setPhosphorescenceDuration] = useState(5); // segundos
    const [enableColorShift, setEnableColorShift] = useState(false);
    const [colorShiftAngle, setColorShiftAngle] = useState(45);

    // Características de autenticación
    const [enableMicrotext, setEnableMicrotext] = useState(false);
    const [microtextContent, setMicrotextContent] = useState('AUTHENTIC');
    const [enableSerialPattern, setEnableSerialPattern] = useState(false);
    const [serialPattern, setSerialPattern] = useState('');

    // Características anti-escáner
    const [enableMoirePattern, setEnableMoirePattern] = useState(false);
    const [moireFrequency, setMoireFrequency] = useState(150);
    const [enableMetameric, setEnableMetameric] = useState(false);

    const canvasRef = useRef(null);

    useEffect(() => {
        if (isOpen && canvasRef.current) {
            updatePreview();
        }
    }, [isOpen, effectType, uvColor, intensity, dotDensity, guillocheWaves, watermarkText, watermarkAngle, gridSpacing]);

    const updatePreview = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        const w = canvas.width;
        const h = canvas.height;

        // Fondo oscuro para simular luz UV
        ctx.fillStyle = '#1a1a2e';
        ctx.fillRect(0, 0, w, h);

        // Aplicar efecto según tipo
        ctx.globalAlpha = intensity;

        switch (effectType) {
            case 'microdots':
                drawMicrodots(ctx, w, h);
                break;
            case 'guilloche':
                drawGuilloche(ctx, w, h);
                break;
            case 'textWatermark':
                drawTextWatermark(ctx, w, h);
                break;
            case 'grid':
                drawGrid(ctx, w, h);
                break;
            default:
                drawMicrodots(ctx, w, h);
        }

        ctx.globalAlpha = 1.0;
    };

    const drawMicrodots = (ctx, w, h) => {
        const scaledDensity = Math.floor(dotDensity / 3); // Escalar para preview
        ctx.fillStyle = uvColor;

        if (dotPattern === 'random') {
            for (let i = 0; i < scaledDensity; i++) {
                const x = Math.random() * w;
                const y = Math.random() * h;
                const radius = dotSize * (0.5 + Math.random() * 0.5);
                ctx.globalAlpha = intensity * (0.5 + Math.random() * 0.5);
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, Math.PI * 2);
                ctx.fill();
            }
        } else if (dotPattern === 'grid') {
            const spacing = Math.sqrt((w * h) / scaledDensity);
            for (let y = 0; y < h; y += spacing) {
                for (let x = 0; x < w; x += spacing) {
                    ctx.beginPath();
                    ctx.arc(x, y, dotSize, 0, Math.PI * 2);
                    ctx.fill();
                }
            }
        } else if (dotPattern === 'spiral') {
            const centerX = w / 2;
            const centerY = h / 2;
            const maxRadius = Math.min(w, h) / 2;

            for (let i = 0; i < scaledDensity; i++) {
                const t = i / scaledDensity;
                const angle = t * Math.PI * 20;
                const radius = t * maxRadius;
                const x = centerX + radius * Math.cos(angle);
                const y = centerY + radius * Math.sin(angle);

                ctx.beginPath();
                ctx.arc(x, y, dotSize, 0, Math.PI * 2);
                ctx.fill();
            }
        }
    };

    const drawGuilloche = (ctx, w, h) => {
        const centerX = w / 2;
        const centerY = h / 2;

        ctx.strokeStyle = uvColor;
        ctx.lineWidth = 1.5;

        for (let layer = 0; layer < guillocheLayers; layer++) {
            const R = Math.min(w, h) * (0.45 - layer * 0.08);
            const steps = 1400;

            ctx.beginPath();
            ctx.globalAlpha = intensity * (0.9 - layer * 0.15);

            for (let i = 0; i <= steps; i++) {
                const t = (i / steps) * Math.PI * 2 * guillocheFrequency;
                const r = R * (1 + guillocheAmplitude * Math.cos(guillocheWaves * t));
                const x = centerX + r * Math.cos(t);
                const y = centerY + r * Math.sin(t);

                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
            }

            ctx.stroke();
        }
    };

    const drawTextWatermark = (ctx, w, h) => {
        ctx.fillStyle = uvColor;
        ctx.font = `700 ${watermarkFontSize}px "Saira Extra Condensed", sans-serif`;

        const textWidth = ctx.measureText(watermarkText).width;
        const stepX = Math.max(120, textWidth + 40);
        const stepY = watermarkSpacing;

        ctx.save();

        for (let y = -h; y < h * 2; y += stepY) {
            for (let x = -w; x < w * 2; x += stepX) {
                const offset = ((y / stepY) % 2 === 0) ? 0 : stepX / 2;

                ctx.save();
                ctx.translate(x + offset, y);
                ctx.rotate((watermarkAngle * Math.PI) / 180);
                ctx.globalAlpha = intensity * 0.35;
                ctx.fillText(watermarkText, 0, 0);
                ctx.restore();
            }
        }

        ctx.restore();
    };

    const drawGrid = (ctx, w, h) => {
        ctx.strokeStyle = uvColor;
        ctx.lineWidth = gridStrokeWidth;
        ctx.globalAlpha = intensity * 0.28;

        // Malla vertical y horizontal
        for (let x = 0; x <= w; x += gridSpacing) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, h);
            ctx.stroke();
        }

        for (let y = 0; y <= h; y += gridSpacing) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(w, y);
            ctx.stroke();
        }

        // Malla diagonal si está habilitada
        if (gridDiagonal) {
            ctx.globalAlpha = intensity * 0.18;
            const diagSpacing = gridSpacing * 1.5;

            for (let x = -h; x < w + h; x += diagSpacing) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x - h, h);
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(x, h);
                ctx.lineTo(x - h, 0);
                ctx.stroke();
            }
        }
    };

    const generateSerialPattern = () => {
        const timestamp = Date.now().toString(36).toUpperCase();
        const random = Math.random().toString(36).substring(2, 10).toUpperCase();
        return `UV-${timestamp}-${random}`;
    };

    const handleGenerate = () => {
        // Crear canvas temporal para renderizar el efecto
        const tempCanvas = document.createElement('canvas');
        const w = parseInt(width);
        const h = parseInt(height);
        tempCanvas.width = w;
        tempCanvas.height = h;
        const ctx = tempCanvas.getContext('2d');

        // Fondo transparente
        ctx.clearRect(0, 0, w, h);

        // Aplicar efecto según tipo
        ctx.globalAlpha = parseFloat(intensity);

        switch (effectType) {
            case 'microdots':
                renderMicrodots(ctx, w, h);
                break;
            case 'guilloche':
                renderGuilloche(ctx, w, h);
                break;
            case 'textWatermark':
                renderTextWatermark(ctx, w, h);
                break;
            case 'grid':
                renderGrid(ctx, w, h);
                break;
        }

        ctx.globalAlpha = 1.0;

        // Convertir canvas a imagen y crear objeto Fabric
        const dataURL = tempCanvas.toDataURL('image/png');

        // Crear configuración completa
        const config = {
            src: dataURL,
            effectType,
            uvColor,
            intensity: parseFloat(intensity),
            width: w,
            height: h,
            customType: 'uvLayer',
            isSecurityFeature: true,
            securityLevel: 'advanced',
            metadata: {
                createdAt: new Date().toISOString(),
                uvWavelength: '365nm',
                visibility: 'UV light only',
                antiCounterfeit: true
            }
        };

        // Añadir configuración específica
        switch (effectType) {
            case 'microdots':
                config.microdots = {
                    density: parseInt(dotDensity),
                    size: parseFloat(dotSize),
                    pattern: dotPattern
                };
                break;
            case 'guilloche':
                config.guilloche = {
                    waves: parseInt(guillocheWaves),
                    frequency: parseInt(guillocheFrequency),
                    amplitude: parseFloat(guillocheAmplitude),
                    layers: parseInt(guillocheLayers)
                };
                break;
            case 'textWatermark':
                config.watermark = {
                    text: watermarkText,
                    angle: parseInt(watermarkAngle),
                    spacing: parseInt(watermarkSpacing),
                    fontSize: parseInt(watermarkFontSize)
                };
                break;
            case 'grid':
                config.grid = {
                    spacing: parseInt(gridSpacing),
                    strokeWidth: parseFloat(gridStrokeWidth),
                    diagonal: gridDiagonal
                };
                break;
        }

        // Características avanzadas
        config.advanced = {
            fluorescence: enableFluorescence,
            fluorescenceColor,
            phosphorescence: enablePhosphorescence,
            phosphorescenceDuration: parseInt(phosphorescenceDuration),
            colorShift: enableColorShift,
            colorShiftAngle: parseInt(colorShiftAngle),
            microtext: enableMicrotext,
            microtextContent,
            serialPattern: enableSerialPattern ? (serialPattern || generateSerialPattern()) : null,
            moirePattern: enableMoirePattern,
            moireFrequency: parseInt(moireFrequency),
            metameric: enableMetameric
        };

        onGenerate(config);
        onClose();
    };

    // Funciones de renderizado (copiar de updatePreview pero sin el fondo oscuro)
    const renderMicrodots = (ctx, w, h) => {
        const scaledDensity = parseInt(dotDensity);
        ctx.fillStyle = uvColor;

        if (dotPattern === 'random') {
            for (let i = 0; i < scaledDensity; i++) {
                const x = Math.random() * w;
                const y = Math.random() * h;
                const radius = parseFloat(dotSize) * (0.5 + Math.random() * 0.5);
                ctx.globalAlpha = parseFloat(intensity) * (0.5 + Math.random() * 0.5);
                ctx.beginPath();
                ctx.arc(x, y, radius, 0, Math.PI * 2);
                ctx.fill();
            }
        } else if (dotPattern === 'grid') {
            const spacing = Math.sqrt((w * h) / scaledDensity);
            for (let y = 0; y < h; y += spacing) {
                for (let x = 0; x < w; x += spacing) {
                    ctx.beginPath();
                    ctx.arc(x, y, parseFloat(dotSize), 0, Math.PI * 2);
                    ctx.fill();
                }
            }
        } else if (dotPattern === 'spiral') {
            const centerX = w / 2;
            const centerY = h / 2;
            const maxRadius = Math.min(w, h) / 2;

            for (let i = 0; i < scaledDensity; i++) {
                const t = i / scaledDensity;
                const angle = t * Math.PI * 20;
                const radius = t * maxRadius;
                const x = centerX + radius * Math.cos(angle);
                const y = centerY + radius * Math.sin(angle);

                ctx.beginPath();
                ctx.arc(x, y, parseFloat(dotSize), 0, Math.PI * 2);
                ctx.fill();
            }
        }
    };

    const renderGuilloche = (ctx, w, h) => {
        const centerX = w / 2;
        const centerY = h / 2;

        ctx.strokeStyle = uvColor;
        ctx.lineWidth = 1.5;

        for (let layer = 0; layer < parseInt(guillocheLayers); layer++) {
            const R = Math.min(w, h) * (0.45 - layer * 0.08);
            const steps = 1400;

            ctx.beginPath();
            ctx.globalAlpha = parseFloat(intensity) * (0.9 - layer * 0.15);

            for (let i = 0; i <= steps; i++) {
                const t = (i / steps) * Math.PI * 2 * parseInt(guillocheFrequency);
                const r = R * (1 + parseFloat(guillocheAmplitude) * Math.cos(parseInt(guillocheWaves) * t));
                const x = centerX + r * Math.cos(t);
                const y = centerY + r * Math.sin(t);

                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
            }

            ctx.stroke();
        }
    };

    const renderTextWatermark = (ctx, w, h) => {
        ctx.fillStyle = uvColor;
        ctx.font = `700 ${parseInt(watermarkFontSize)}px "Saira Extra Condensed", sans-serif`;

        const textWidth = ctx.measureText(watermarkText).width;
        const stepX = Math.max(120, textWidth + 40);
        const stepY = parseInt(watermarkSpacing);

        ctx.save();

        for (let y = -h; y < h * 2; y += stepY) {
            for (let x = -w; x < w * 2; x += stepX) {
                const offset = ((y / stepY) % 2 === 0) ? 0 : stepX / 2;

                ctx.save();
                ctx.translate(x + offset, y);
                ctx.rotate((parseInt(watermarkAngle) * Math.PI) / 180);
                ctx.globalAlpha = parseFloat(intensity) * 0.35;
                ctx.fillText(watermarkText, 0, 0);
                ctx.restore();
            }
        }

        ctx.restore();
    };

    const renderGrid = (ctx, w, h) => {
        ctx.strokeStyle = uvColor;
        ctx.lineWidth = parseFloat(gridStrokeWidth);
        ctx.globalAlpha = parseFloat(intensity) * 0.28;

        // Malla vertical y horizontal
        for (let x = 0; x <= w; x += parseInt(gridSpacing)) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, h);
            ctx.stroke();
        }

        for (let y = 0; y <= h; y += parseInt(gridSpacing)) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(w, y);
            ctx.stroke();
        }

        // Malla diagonal si está habilitada
        if (gridDiagonal) {
            ctx.globalAlpha = parseFloat(intensity) * 0.18;
            const diagSpacing = parseInt(gridSpacing) * 1.5;

            for (let x = -h; x < w + h; x += diagSpacing) {
                ctx.beginPath();
                ctx.moveTo(x, 0);
                ctx.lineTo(x - h, h);
                ctx.stroke();

                ctx.beginPath();
                ctx.moveTo(x, h);
                ctx.lineTo(x - h, 0);
                ctx.stroke();
            }
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl shadow-2xl w-full max-w-6xl overflow-hidden border border-slate-700">
                {/* Header */}
                <div className="p-6 border-b border-slate-700 bg-gradient-to-r from-purple-900/50 to-indigo-900/50">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                                <span className="text-3xl">🌈</span>
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold text-white">Capa UV Invisible (Ultravioleta)</h3>
                                <p className="text-sm text-purple-300">Características de seguridad invisibles • Solo visibles bajo luz UV 365nm</p>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-slate-700 rounded-lg"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[75vh] overflow-y-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Columna 1 - Tipo de Efecto */}
                        <div className="space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-purple-400 mb-3 flex items-center">
                                    <span className="mr-2">✨</span> Tipo de Efecto UV
                                </h4>

                                <div className="space-y-3">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Efecto Principal</label>
                                        <select
                                            value={effectType}
                                            onChange={(e) => setEffectType(e.target.value)}
                                            className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-purple-500"
                                        >
                                            <option value="microdots">🔵 Micro Puntos Fluorescentes</option>
                                            <option value="guilloche">🌀 Patrón Guilloche</option>
                                            <option value="textWatermark">📝 Marca de Agua Tipográfica</option>
                                            <option value="grid">⊞ Malla Anti-Copia</option>
                                        </select>
                                    </div>

                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Ancho (px)</label>
                                            <input
                                                type="number"
                                                value={width}
                                                onChange={(e) => setWidth(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Alto (px)</label>
                                            <input
                                                type="number"
                                                value={height}
                                                onChange={(e) => setHeight(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-purple-500"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">Color UV</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { value: '#7c3aed', label: 'Violeta UV', color: 'bg-purple-500' },
                                                { value: '#22d3ee', label: 'Cian Fluorescente', color: 'bg-cyan-400' },
                                                { value: '#84cc16', label: 'Verde Neón', color: 'bg-lime-500' },
                                                { value: '#f59e0b', label: 'Ámbar UV', color: 'bg-amber-500' }
                                            ].map(c => (
                                                <button
                                                    key={c.value}
                                                    onClick={() => setUvColor(c.value)}
                                                    className={`p-2 rounded-lg border-2 transition-all ${uvColor === c.value
                                                        ? 'border-white shadow-lg'
                                                        : 'border-slate-600 hover:border-slate-500'
                                                        }`}
                                                >
                                                    <div className={`w-full h-6 ${c.color} rounded mb-1`}></div>
                                                    <span className="text-xs text-slate-300">{c.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                        <input
                                            type="color"
                                            value={uvColor}
                                            onChange={(e) => setUvColor(e.target.value)}
                                            className="w-full h-10 mt-2 bg-slate-900 border border-slate-600 rounded-lg cursor-pointer"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-slate-300 mb-2">
                                            Intensidad: {Math.round(intensity * 100)}%
                                        </label>
                                        <input
                                            type="range"
                                            min="0.1"
                                            max="1"
                                            step="0.05"
                                            value={intensity}
                                            onChange={(e) => setIntensity(parseFloat(e.target.value))}
                                            className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Preview */}
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-cyan-400 mb-3 flex items-center">
                                    <span className="mr-2">👁️</span> Simulación UV (Luz Negra)
                                </h4>
                                <div className="w-full h-48 rounded-lg overflow-hidden border border-purple-500/50 shadow-lg shadow-purple-500/20">
                                    <canvas ref={canvasRef} width="400" height="192" className="w-full h-full" />
                                </div>
                                <p className="text-xs text-slate-500 mt-2">⚠️ Vista previa simulada. El efecto real solo es visible bajo luz UV 365nm</p>
                            </div>
                        </div>

                        {/* Columna 2 - Configuración Específica */}
                        <div className="space-y-4">
                            {effectType === 'microdots' && (
                                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                    <h4 className="text-sm font-bold text-blue-400 mb-3 flex items-center">
                                        <span className="mr-2">🔵</span> Configuración de Micro Puntos
                                    </h4>

                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Densidad: {dotDensity} puntos
                                            </label>
                                            <input
                                                type="range"
                                                min="300"
                                                max="3000"
                                                step="100"
                                                value={dotDensity}
                                                onChange={(e) => setDotDensity(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Tamaño de Punto: {dotSize}px
                                            </label>
                                            <input
                                                type="range"
                                                min="0.2"
                                                max="3"
                                                step="0.1"
                                                value={dotSize}
                                                onChange={(e) => setDotSize(parseFloat(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Patrón de Distribución</label>
                                            <select
                                                value={dotPattern}
                                                onChange={(e) => setDotPattern(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="random">Aleatorio (Cuántico)</option>
                                                <option value="grid">Cuadrícula Regular</option>
                                                <option value="spiral">Espiral Logarítmica</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {effectType === 'guilloche' && (
                                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                    <h4 className="text-sm font-bold text-indigo-400 mb-3 flex items-center">
                                        <span className="mr-2">🌀</span> Configuración de Guilloche
                                    </h4>

                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Ondas: {guillocheWaves}
                                            </label>
                                            <input
                                                type="range"
                                                min="3"
                                                max="15"
                                                value={guillocheWaves}
                                                onChange={(e) => setGuillocheWaves(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Frecuencia: {guillocheFrequency}
                                            </label>
                                            <input
                                                type="range"
                                                min="5"
                                                max="30"
                                                value={guillocheFrequency}
                                                onChange={(e) => setGuillocheFrequency(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Amplitud: {guillocheAmplitude.toFixed(2)}
                                            </label>
                                            <input
                                                type="range"
                                                min="0.1"
                                                max="0.8"
                                                step="0.01"
                                                value={guillocheAmplitude}
                                                onChange={(e) => setGuillocheAmplitude(parseFloat(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Capas: {guillocheLayers}
                                            </label>
                                            <input
                                                type="range"
                                                min="1"
                                                max="5"
                                                value={guillocheLayers}
                                                onChange={(e) => setGuillocheLayers(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {effectType === 'textWatermark' && (
                                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                    <h4 className="text-sm font-bold text-green-400 mb-3 flex items-center">
                                        <span className="mr-2">📝</span> Configuración de Marca de Agua
                                    </h4>

                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">Texto de Marca de Agua</label>
                                            <input
                                                type="text"
                                                value={watermarkText}
                                                onChange={(e) => setWatermarkText(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-green-500"
                                                placeholder="SECURITY • ORIGINAL"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Ángulo: {watermarkAngle}°
                                            </label>
                                            <input
                                                type="range"
                                                min="-60"
                                                max="60"
                                                value={watermarkAngle}
                                                onChange={(e) => setWatermarkAngle(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Espaciado Vertical: {watermarkSpacing}px
                                            </label>
                                            <input
                                                type="range"
                                                min="24"
                                                max="120"
                                                value={watermarkSpacing}
                                                onChange={(e) => setWatermarkSpacing(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Tamaño de Fuente: {watermarkFontSize}px
                                            </label>
                                            <input
                                                type="range"
                                                min="12"
                                                max="48"
                                                value={watermarkFontSize}
                                                onChange={(e) => setWatermarkFontSize(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {effectType === 'grid' && (
                                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                    <h4 className="text-sm font-bold text-amber-400 mb-3 flex items-center">
                                        <span className="mr-2">⊞</span> Configuración de Malla
                                    </h4>

                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Espaciado: {gridSpacing}px
                                            </label>
                                            <input
                                                type="range"
                                                min="5"
                                                max="30"
                                                value={gridSpacing}
                                                onChange={(e) => setGridSpacing(parseInt(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                                Grosor de Línea: {gridStrokeWidth.toFixed(1)}px
                                            </label>
                                            <input
                                                type="range"
                                                min="0.5"
                                                max="3"
                                                step="0.1"
                                                value={gridStrokeWidth}
                                                onChange={(e) => setGridStrokeWidth(parseFloat(e.target.value))}
                                                className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>

                                        <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={gridDiagonal}
                                                onChange={(e) => setGridDiagonal(e.target.checked)}
                                                className="w-4 h-4 text-amber-600 bg-slate-900 border-slate-600 rounded focus:ring-amber-500"
                                            />
                                            <span className="text-sm text-slate-300">Añadir malla diagonal anti-copia</span>
                                        </label>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Columna 3 - Características Avanzadas */}
                        <div className="space-y-4">
                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-red-400 mb-3 flex items-center">
                                    <span className="mr-2">🔬</span> Propiedades Fotoluminiscentes
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableFluorescence}
                                            onChange={(e) => setEnableFluorescence(e.target.checked)}
                                            className="w-4 h-4 text-red-600 bg-slate-900 border-slate-600 rounded focus:ring-red-500"
                                        />
                                        <span className="text-sm text-slate-300">💡 Fluorescencia (brilla bajo UV)</span>
                                    </label>

                                    {enableFluorescence && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Color de Fluorescencia</label>
                                            <input
                                                type="color"
                                                value={fluorescenceColor}
                                                onChange={(e) => setFluorescenceColor(e.target.value)}
                                                className="w-full h-8 bg-slate-900 border border-slate-600 rounded cursor-pointer"
                                            />
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enablePhosphorescence}
                                            onChange={(e) => setEnablePhosphorescence(e.target.checked)}
                                            className="w-4 h-4 text-red-600 bg-slate-900 border-slate-600 rounded focus:ring-red-500"
                                        />
                                        <span className="text-sm text-slate-300">⏱️ Fosforescencia (brilla en oscuridad)</span>
                                    </label>

                                    {enablePhosphorescence && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">
                                                Duración: {phosphorescenceDuration}s
                                            </label>
                                            <input
                                                type="range"
                                                min="1"
                                                max="30"
                                                value={phosphorescenceDuration}
                                                onChange={(e) => setPhosphorescenceDuration(parseInt(e.target.value))}
                                                className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableColorShift}
                                            onChange={(e) => setEnableColorShift(e.target.checked)}
                                            className="w-4 h-4 text-red-600 bg-slate-900 border-slate-600 rounded focus:ring-red-500"
                                        />
                                        <span className="text-sm text-slate-300">🌈 Cambio de color angular (OVI)</span>
                                    </label>

                                    {enableColorShift && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">
                                                Ángulo de Cambio: {colorShiftAngle}°
                                            </label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="90"
                                                value={colorShiftAngle}
                                                onChange={(e) => setColorShiftAngle(parseInt(e.target.value))}
                                                className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                                <h4 className="text-sm font-bold text-yellow-400 mb-3 flex items-center">
                                    <span className="mr-2">🛡️</span> Anti-Escáner / Anti-Copia
                                </h4>

                                <div className="space-y-3">
                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableMoirePattern}
                                            onChange={(e) => setEnableMoirePattern(e.target.checked)}
                                            className="w-4 h-4 text-yellow-600 bg-slate-900 border-slate-600 rounded focus:ring-yellow-500"
                                        />
                                        <span className="text-sm text-slate-300">📸 Patrón Moiré (anti-escáner)</span>
                                    </label>

                                    {enableMoirePattern && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">
                                                Frecuencia: {moireFrequency} LPI
                                            </label>
                                            <input
                                                type="range"
                                                min="100"
                                                max="300"
                                                step="10"
                                                value={moireFrequency}
                                                onChange={(e) => setMoireFrequency(parseInt(e.target.value))}
                                                className="w-full h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                                            />
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableMetameric}
                                            onChange={(e) => setEnableMetameric(e.target.checked)}
                                            className="w-4 h-4 text-yellow-600 bg-slate-900 border-slate-600 rounded focus:ring-yellow-500"
                                        />
                                        <span className="text-sm text-slate-300">🎨 Tinta Metamérica</span>
                                    </label>

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableMicrotext}
                                            onChange={(e) => setEnableMicrotext(e.target.checked)}
                                            className="w-4 h-4 text-yellow-600 bg-slate-900 border-slate-600 rounded focus:ring-yellow-500"
                                        />
                                        <span className="text-sm text-slate-300">🔤 Microtexto Integrado</span>
                                    </label>

                                    {enableMicrotext && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Contenido del Microtexto</label>
                                            <input
                                                type="text"
                                                value={microtextContent}
                                                onChange={(e) => setMicrotextContent(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs"
                                                placeholder="AUTHENTIC"
                                            />
                                        </div>
                                    )}

                                    <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/30 rounded cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={enableSerialPattern}
                                            onChange={(e) => setEnableSerialPattern(e.target.checked)}
                                            className="w-4 h-4 text-yellow-600 bg-slate-900 border-slate-600 rounded focus:ring-yellow-500"
                                        />
                                        <span className="text-sm text-slate-300">🔢 Patrón Serial Único</span>
                                    </label>

                                    {enableSerialPattern && (
                                        <div className="ml-6 p-3 bg-slate-900/50 rounded-lg border border-slate-600">
                                            <label className="block text-xs text-slate-400 mb-1">Patrón Serial (auto-generado si vacío)</label>
                                            <input
                                                type="text"
                                                value={serialPattern}
                                                onChange={(e) => setSerialPattern(e.target.value)}
                                                className="w-full bg-slate-900 border border-slate-600 rounded px-2 py-1 text-white text-xs font-mono"
                                                placeholder="UV-XXXXXX-XXXX"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Información Técnica */}
                            <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/30 rounded-lg p-4 border border-purple-700/50">
                                <h4 className="text-sm font-bold text-purple-300 mb-2">ℹ️ Especificaciones UV</h4>
                                <div className="space-y-1 text-xs text-slate-400">
                                    <p>• <strong>Longitud de onda:</strong> 365nm (UV-A)</p>
                                    <p>• <strong>Visibilidad:</strong> Invisible a simple vista</p>
                                    <p>• <strong>Verificación:</strong> Lámpara UV de luz negra</p>
                                    <p>• <strong>Aplicaciones:</strong> Billetes, pasaportes, eventos</p>
                                    <p>• <strong>Nivel de seguridad:</strong> Avanzado / Clase A+</p>
                                    <p>• <strong>Resistencia:</strong> Permanente, no copiable</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-700 bg-slate-900/50 flex justify-between items-center">
                    <div className="text-xs text-slate-500">
                        <p>🌈 Tecnología UV de Seguridad Profesional</p>
                        <p>Requiere tintas fluorescentes y lámpara UV 365nm para verificación</p>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            className="px-6 py-2.5 text-sm font-medium text-slate-300 bg-slate-800 border border-slate-600 rounded-lg hover:bg-slate-700 transition-colors"
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleGenerate}
                            className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 via-fuchsia-600 to-indigo-600 rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg shadow-purple-500/50"
                        >
                            🌈 Crear Capa UV de Seguridad
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UVLayerModal;
